<template>
  <v-row class="fill-height" no-gutters>
    <v-col cols="8" class="left-container" ref="containerRef">
      <v-menu
        v-if="state === 'in_configuration'"
        class="drawing-menu"
        :value="addingRoom || updatingRoom"
        :position-x="menuX"
        :position-y="menuY"
        absolute
        offset-y
        :close-on-click="false"
        :close-on-content-click="false"
        z-index="200"
      >
        <drawing-menu />
      </v-menu>

      <plan-area />
      <left-sidebar :buttonVisible="true" :project="project" />
    </v-col>
    <niko-modal-wrapper
      v-if="newState"
      :title="
        $t(`project.change_state.notify.${newState}.title`, {
          project: project.name,
        })
      "
      :primaryButtonText="$t('project.change_state.notify.button')"
      :showModal="showStateChangedModal"
      :persistent="true"
      @onSubmit="goToDashboard"
    >
      <p class="body2 mt-4 mb-7">
        {{ $t(`project.change_state.notify.${newState}.description`) }}
      </p>
    </niko-modal-wrapper>
    <right-sidebar />
    <v-snackbar
      :timeout="5000"
      v-model="reminderSnackbar"
      color="#00AEEF"
      top
      absolute
      centered
    >
      <p class="subtitle-1 mr-3">{{ reminderSnackbarText }}</p>
      <v-icon class="snackbar-icon" @click="reminderSnackbar = false">
        $iconClose
      </v-icon>
    </v-snackbar>
    <v-snackbar
      :timeout="5000"
      v-model="duplicateAddedSnackbar"
      color="#00AEEF"
      top
      absolute
      centered
    >
      <p class="subtitle-1 mr-3">
        {{
          $t("project.adding_duplicate.snackbar.duplicate_added", {
            project: name,
          })
        }}
      </p>
      <v-icon class="snackbar-icon" @click="duplicateAddedSnackbar = false">
        $iconClose
      </v-icon>
    </v-snackbar>

    <niko-modal-wrapper
      persistent
      :showModal="showUpgradeModal"
      :title="$t('premium.modal.title')"
      :primaryButtonText="$t('premium.modal.primary_button')"
      :secondaryButtonText="$t('premium.modal.secondary_button')"
      @onClose="goToDashboard"
      @onSubmit="openFastSpringPopup"
      ><p class="body1 mt-9 mb-7">
        {{ $t("premium.modal.shared_project_text") }}
      </p></niko-modal-wrapper
    >
  </v-row>
</template>

<script>
import { mapActions, mapMutations, mapState } from "vuex";
import DrawingMenu from "@/components/plan/DrawingMenu";
import PlanArea from "@/components/plan/PlanArea";
import RightSidebar from "@/components/sidebar/RightSidebar";
import LeftSidebar from "@/components/sidebar/LeftSidebar";
import NikoModalWrapper from "@/components/UI/NikoModalWrapper";
import { eventBus } from "@/components/EventBus";

export default {
  name: "project",
  data: () => ({
    menuX: null,
    menuY: null,
    reminderSnackbar: false,
    duplicateAddedSnackbar: false,
    showStateChangedModal: false,
    showUpgradeModal: false,
  }),
  components: {
    DrawingMenu,
    PlanArea,
    RightSidebar,
    LeftSidebar,
    NikoModalWrapper,
  },
  computed: {
    ...mapState("authStore", ["fastspringAccountId"]),
    ...mapState("projectStore", [
      "project_id",
      "name",
      "buildings",
      "state",
      "users",
      "date_shared",
      "newState",
      "maintenance",
      "standard",
    ]),
    ...mapState("projectsStore", ["projects"]),
    ...mapState("roomStore", ["addingRoom", "updatingRoom", "isDrawing"]),
    ...mapState("authStore", [
      "activeSubscription",
      "email",
      "firstname",
      "lastname",
    ]),
    project() {
      return {
        id: this.project_id,
        name: this.name,
        state: this.state,
        buildings: this.buildings,
        date_shared: this.date_shared,
      };
    },
    reminderSnackbarText() {
      return this.state && this.state !== "completed"
        ? this.$t(`project.notification.reminder.${this.state}`)
        : "";
    },
  },
  methods: {
    ...mapActions("projectStore", [
      "getProjectById",
      "subscribeSetState",
      "unsubscribeSetState",
      "subscribeRevokeAccess",
      "unsubscribeRevokeAccess",
      "getMaintenanceFlag",
      "getImageOptimizationFlag",
    ]),
    ...mapActions("projectsStore", ["getAllProjects"]),
    ...mapActions("authStore", ["getUserDetails", "checkPremiumStatus"]),
    ...mapActions("roomStore", ["getRoomTypes"]),
    ...mapMutations("loaderStore", ["setIsLoading"]),
    ...mapMutations("roomStore", [
      "undoLastCorner",
      "setIsDrawing",
      "setAddingRoom",
    ]),
    calculateMenuPosition() {
      this.menuY = this.$refs.containerRef.clientHeight / 2 - 152 / 2;
      this.menuX = this.$refs.containerRef.clientWidth - 16 - 64;
    },
    goToDashboard() {
      this.$router.push("/");
    },
    openFastSpringPopup() {
      window.fastspring.builder.recognize({
        email: this.email,
        firstName: this.firstname,
        lastName: this.lastname,
      });
      window.fastspring.builder.add("fa-assistent-premium");
      window.fastspring.builder.checkout();
    },
  },
  watch: {
    users() {
      if (
        this.users !== null &&
        this.users.length > 0 &&
        this.state !== "completed"
      ) {
        this.reminderSnackbar = true;
      }
    },
    newState() {
      this.showStateChangedModal = true;
    },
    maintenance() {
      if (this.maintenance) {
        this.$router.push("/maintenance");
      }
    },
    project_id() {
      if (
        this.project_id &&
        !this.activeSubscription &&
        this.projects.findIndex(
          (project) => project.project_id === this.project_id
        ) <
          this.projects.length - 3
      ) {
        this.showUpgradeModal = true;
      }
    },
    activeSubscription() {
      if (this.activeSubscription) {
        this.showUpgradeModal = false;
      }
    },
  },
  mounted() {
    this.calculateMenuPosition();
    eventBus.$on(
      "newDuplicateAdded",
      () => (this.duplicateAddedSnackbar = true)
    );
  },
  async created() {
    await this.getMaintenanceFlag();
    if (this.maintenance) {
      this.$router.push("/maintenance");
    }
    await this.getImageOptimizationFlag();
    this.setAddingRoom(false);
    window.addEventListener("resize", this.calculateMenuPosition);
    const project_id = this.$route.params.id;
    if (this.buildings.length === 0) {
      this.setIsLoading(true);
      this.getProjectById(project_id).then(() => {
        this.getRoomTypes(this.standard);
        this.setIsLoading(false);
      });
    } else {
      this.getRoomTypes(this.standard);
    }
    this.subscribeSetState(project_id);
    await this.getUserDetails();
    this.subscribeRevokeAccess(project_id);
    this.fastspringAccountId && this.checkPremiumStatus();
    await this.getAllProjects();
  },
  destroyed() {
    window.removeEventListener("resize", this.calculateMenuPosition);
    eventBus.$off("newDuplicateAdded");
  },
  beforeRouteLeave(to, from, next) {
    this.unsubscribeSetState();
    this.unsubscribeRevokeAccess();
    next();
  },
};
</script>

<style lang="scss">
.v-menu__content {
  border: none !important;
}
</style>
